import EventIcon from "@mui/icons-material/Event";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PeopleIcon from "@mui/icons-material/People";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import LanguageIcon from "@mui/icons-material/Language";

type CountBag = Record<string, any> | undefined;

const VIEW_TABS = [
  { key: "all", label: "All", viewKey: "all" },
  { key: "active", label: "Active", viewKey: "active" },
  { key: "completed", label: "Completed", viewKey: "completed" },
  {
    key: "cancelled_noshow",
    label: "Cancelled & No-Show",
    viewKey: "cancelled_noshow",
  },
] as const;

const STATUS_CARDS: Array<{
  key: string;
  label: string;
  statusKey: string;
  icon: typeof EventAvailableIcon;
  bg: string;
  text: string;
  iconBg: string;
  ring: string;
}> = [
  {
    key: "upcoming",
    label: "Upcoming",
    statusKey: "upcoming",
    icon: EventAvailableIcon,
    bg: "bg-yellow-100 dark:bg-yellow-900/40",
    text: "text-yellow-800 dark:text-yellow-300",
    iconBg: "bg-yellow-500",
    ring: "ring-yellow-400/60",
  },
  {
    key: "in_queue",
    label: "In Queue",
    statusKey: "in_queue",
    icon: PeopleIcon,
    bg: "bg-sky-100 dark:bg-sky-900/40",
    text: "text-sky-700 dark:text-sky-300",
    iconBg: "bg-sky-500",
    ring: "ring-sky-400/60",
  },
  {
    key: "ongoing",
    label: "Ongoing",
    statusKey: "ongoing",
    icon: PlayCircleIcon,
    bg: "bg-lime-100 dark:bg-lime-900/40",
    text: "text-lime-700 dark:text-lime-300",
    iconBg: "bg-lime-500",
    ring: "ring-lime-400/60",
  },
  {
    key: "complete",
    label: "Complete",
    statusKey: "complete",
    icon: CheckCircleIcon,
    bg: "bg-emerald-100 dark:bg-emerald-900/40",
    text: "text-emerald-700 dark:text-emerald-300",
    iconBg: "bg-emerald-500",
    ring: "ring-emerald-400/60",
  },
  {
    key: "cancelled",
    label: "Cancelled",
    statusKey: "cancelled",
    icon: CancelIcon,
    bg: "bg-red-100 dark:bg-red-900/40",
    text: "text-red-700 dark:text-red-300",
    iconBg: "bg-red-500",
    ring: "ring-red-400/60",
  },
  {
    key: "no_show",
    label: "No-Show",
    statusKey: "no_show",
    icon: VisibilityOffIcon,
    bg: "bg-slate-100 dark:bg-slate-700",
    text: "text-slate-700 dark:text-slate-200",
    iconBg: "bg-slate-500",
    ring: "ring-slate-400/60",
  },
];

const SOURCE_CHIPS: Array<{
  key: string;
  label: string;
  sourceKey: string;
  icon: typeof ScheduleIcon;
  activeClass: string;
  idleClass: string;
}> = [
  {
    key: "pre_booked",
    label: "Schedule",
    sourceKey: "pre_booked",
    icon: ScheduleIcon,
    activeClass:
      "bg-orange-500 text-white border-orange-500 shadow-sm",
    idleClass:
      "bg-white dark:bg-slate-700 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-700 hover:border-orange-400",
  },
  {
    key: "walk_in",
    label: "Walk-in",
    sourceKey: "walk_in",
    icon: DirectionsWalkIcon,
    activeClass: "bg-teal-500 text-white border-teal-500 shadow-sm",
    idleClass:
      "bg-white dark:bg-slate-700 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-700 hover:border-teal-400",
  },
  {
    key: "call_in",
    label: "Call-in",
    sourceKey: "call_in",
    icon: PhoneInTalkIcon,
    activeClass: "bg-cyan-500 text-white border-cyan-500 shadow-sm",
    idleClass:
      "bg-white dark:bg-slate-700 text-cyan-700 dark:text-cyan-300 border-cyan-200 dark:border-cyan-700 hover:border-cyan-400",
  },
  {
    key: "online",
    label: "Online",
    sourceKey: "online",
    icon: LanguageIcon,
    activeClass: "bg-indigo-500 text-white border-indigo-500 shadow-sm",
    idleClass:
      "bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700 hover:border-indigo-400",
  },
];

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function resolveViewCount(count: CountBag, viewKey: string): number {
  const byView = count?.by_view;
  if (byView && typeof byView === "object") {
    if (viewKey === "all") return num(byView.all ?? count?.appointments);
    if (viewKey === "active") return num(byView.active);
    if (viewKey === "completed") return num(byView.completed ?? byView.complete);
    if (viewKey === "cancelled_noshow") {
      return num(
        byView.cancelled_noshow ??
          num(byView.cancelled) + num(byView.no_show)
      );
    }
  }
  if (viewKey === "all") {
    return num(count?.appointments ?? count?.total);
  }
  if (viewKey === "active") {
    if (count?.active != null) return num(count.active);
    return (
      num(count?.upcoming ?? count?.pending ?? count?.appointment) +
      num(count?.in_queue ?? count?.inqueue) +
      num(count?.ongoing) +
      num(count?.scheduled)
    );
  }
  if (viewKey === "completed") {
    return num(count?.complete ?? count?.completed);
  }
  if (viewKey === "cancelled_noshow") {
    return num(count?.cancelled ?? count?.canceled) + num(count?.no_show ?? count?.noshow);
  }
  return 0;
}

function resolveStatusCount(count: CountBag, statusKey: string): number {
  const byStatus = count?.by_status;
  if (byStatus && typeof byStatus === "object") {
    if (statusKey === "in_queue") return num(byStatus.in_queue ?? byStatus.inqueue);
    if (statusKey === "complete") return num(byStatus.complete ?? byStatus.completed);
    if (statusKey === "cancelled") return num(byStatus.cancelled ?? byStatus.canceled);
    if (statusKey === "no_show") return num(byStatus.no_show ?? byStatus.noshow);
    return num(byStatus[statusKey]);
  }
  if (statusKey === "upcoming") {
    return num(count?.upcoming ?? count?.pending ?? count?.appointment);
  }
  if (statusKey === "in_queue") return num(count?.in_queue ?? count?.inqueue);
  if (statusKey === "complete") return num(count?.complete ?? count?.completed);
  if (statusKey === "cancelled") return num(count?.cancelled ?? count?.canceled);
  if (statusKey === "no_show") return num(count?.no_show ?? count?.noshow);
  return num(count?.[statusKey]);
}

function resolveSourceCount(count: CountBag, sourceKey: string): number {
  const bySource = count?.by_source;
  if (bySource && typeof bySource === "object") {
    if (sourceKey === "pre_booked") {
      return num(bySource.pre_booked ?? bySource.schedule);
    }
    if (sourceKey === "walk_in") return num(bySource.walk_in ?? bySource.walkin);
    if (sourceKey === "call_in") return num(bySource.call_in ?? bySource.callin);
    return num(bySource[sourceKey]);
  }
  if (sourceKey === "pre_booked") return num(count?.pre_booked ?? count?.schedule);
  if (sourceKey === "walk_in") return num(count?.walk_in ?? count?.walkin);
  if (sourceKey === "call_in") return num(count?.call_in ?? count?.callin);
  return num(count?.[sourceKey]);
}

function AppointmentCounts({
  count,
  activeView = "active",
  activeStatus = "",
  activeSource = "",
  onViewSelect,
  onStatusSelect,
  onSourceSelect,
}: {
  count: CountBag;
  /** @deprecated unused — kept so older call sites type-check during migrate */
  compact?: boolean;
  activeView?: string;
  activeStatus?: string;
  activeSource?: string;
  onViewSelect?: (viewKey: string) => void;
  /** Click again clears (pass empty string). */
  onStatusSelect?: (statusKey: string) => void;
  /** Click toggles source filter. */
  onSourceSelect?: (sourceKey: string) => void;
  /** @deprecated */
  activeKey?: string;
  /** @deprecated */
  onSelect?: (key: string) => void;
  /** @deprecated */
  selectableKeys?: string[];
}) {
  return (
    <div className="space-y-3">
      {/* Row 1 — view tabs */}
      <div className="flex flex-wrap gap-1.5 p-1 bg-slate-100 dark:bg-slate-700/80 rounded-xl">
        {VIEW_TABS.map(({ key, label, viewKey }) => {
          const isActive = activeView === key;
          const n = resolveViewCount(count, viewKey);
          return (
            <button
              key={key}
              type="button"
              onClick={() => onViewSelect?.(key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                isActive
                  ? "bg-white dark:bg-slate-600 text-indigo-600 dark:text-indigo-300 shadow-sm"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              }`}
              aria-pressed={isActive}
            >
              {label}
              <span className="ml-1 tabular-nums opacity-80">({n})</span>
            </button>
          );
        })}
      </div>

      {/* Row 2 — status cards (mutually exclusive) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-1.5">
        {STATUS_CARDS.map(
          ({ key, label, statusKey, icon: Icon, bg, text, iconBg, ring }) => {
            const isActive = activeStatus === key;
            const n = resolveStatusCount(count, statusKey);
            return (
              <button
                key={key}
                type="button"
                onClick={() =>
                  onStatusSelect?.(isActive ? "" : key)
                }
                className={[
                  bg,
                  "rounded-lg px-2 py-2 flex items-center gap-1.5 min-w-0 transition-all duration-200 cursor-pointer hover:scale-[1.02] hover:shadow-md active:scale-[0.98]",
                  isActive ? `ring-2 ${ring} shadow-md scale-[1.02]` : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-pressed={isActive}
                title={
                  isActive
                    ? `Clear ${label.toLowerCase()} filter`
                    : `Show ${label.toLowerCase()} appointments`
                }
              >
                <div
                  className={`hidden sm:flex w-6 h-6 shrink-0 ${iconBg} rounded-md items-center justify-center shadow-sm`}
                >
                  <Icon className="text-white" sx={{ fontSize: 14 }} />
                </div>
                <div className="min-w-0 text-left">
                  <p
                    className={`text-sm sm:text-base font-bold ${text} leading-none tabular-nums`}
                  >
                    {n}
                  </p>
                  <p
                    className={`text-[8px] sm:text-[9px] font-medium ${text} uppercase tracking-wide truncate`}
                  >
                    {label}
                  </p>
                </div>
              </button>
            );
          }
        )}
      </div>

      {/* Row 3 — source chips (combinable with status; never mixed into status row) */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mr-0.5 flex items-center gap-1">
          <EventIcon sx={{ fontSize: 12 }} />
          Booked
        </span>
        {SOURCE_CHIPS.map(
          ({ key, label, sourceKey, icon: Icon, activeClass, idleClass }) => {
            const isActive = activeSource === key;
            const n = resolveSourceCount(count, sourceKey);
            return (
              <button
                key={key}
                type="button"
                onClick={() =>
                  onSourceSelect?.(isActive ? "" : key)
                }
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                  isActive ? activeClass : idleClass
                }`}
                aria-pressed={isActive}
                title={
                  isActive
                    ? `Clear ${label} source filter`
                    : `Filter by ${label}`
                }
              >
                <Icon sx={{ fontSize: 14 }} />
                {label}
                <span className="tabular-nums opacity-90">({n})</span>
              </button>
            );
          }
        )}
      </div>
    </div>
  );
}

export default AppointmentCounts;
